import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import ClaimModal from '../components/ClaimModal';
import StatusLight from './StatusLight';
import usePermissions from '../utils/usePermissions';
import PaginationControls from '../components/PaginationControls';
import usePagination from '../utils/usePagination';
import { formatCurrency, getCurrencyPrefixFromStorage, getNumberingSystemFromStorage } from '../utils/numberingSystem';
import '../styles/CustomerList.css';
import '../styles/RecentDocuments.css';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';

const ClaimsListPage = () => {
  const { can } = usePermissions('claims');
  const { showInlineNotice } = useInlineNotice();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ status: null });
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [isClaimViewOpen, setIsClaimViewOpen] = useState(false);
  const [isClaimEditOpen, setIsClaimEditOpen] = useState(false);
  const [claimToView, setClaimToView] = useState(null);
  const [claimToEdit, setClaimToEdit] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role || 'user');
        setCurrentUserId(userData.id || userData._id || null);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  const fetchClaims = async (searchTerm = '') => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/claims`, {
        params: searchTerm ? { search: searchTerm } : undefined
      });
      setClaims(response.data || []);
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Failed to load claims');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    fetchClaims(activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchClaims(activeSearchTerm);
  };

  const handleCheckboxChange = (claimId) => {
    setSelectedRows(prev =>
      prev.includes(claimId)
        ? []
        : [claimId]
    );
  };

  const getSelectedClaim = () => claims.find(c => c._id === selectedRows[0]);

  const handleViewClaim = async () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim) return;

    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/claims/${claim._id}`);
      setClaimToView(response.data);
      setIsClaimViewOpen(true);
    } catch (err) {
      console.error('Error fetching claim details:', err);
      setError(err.response?.data?.error || 'Failed to load claim');
    }
  };

  const handleEditClaim = async () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim) return;

    const isOwner = claim.createdById === currentUserId;
    if (claim.status !== 'Draft' || !isOwner || !canEdit) return;

    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/claims/${claim._id}`);
      setClaimToEdit(response.data);
      setIsClaimEditOpen(true);
    } catch (err) {
      console.error('Error fetching claim details:', err);
      setError(err.response?.data?.error || 'Failed to load claim');
    }
  };

  const handleSubmitClaim = async () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim) return;

    const isOwner = claim.createdById === currentUserId;
    if (!canSubmit || claim.status !== 'Draft' || !isOwner) return;

    try {
      await axios.post(`${API_BASE_URL}/claims/${claim._id}/submit`);
      const label = claim?.claimNumber || claim?._id || 'claim';
      showInlineNotice(`Claim ${label} submitted successfully.`, { scope: 'claims' });
      await fetchClaims(activeSearchTerm);
      setSelectedRows([]);
    } catch (err) {
      console.error('Error submitting claim:', err);
      setError(err.response?.data?.error || 'Failed to submit claim');
      showInlineNotice('Failed to submit claim.', { scope: 'claims', type: 'error' });
    }
  };

  const handleApproveClaim = async () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim || claim.status !== 'Submitted') return;
    if (!canApprove) return;

    try {
      await axios.post(`${API_BASE_URL}/claims/${claim._id}/approve`);
      const label = claim?.claimNumber || claim?._id || 'claim';
      showInlineNotice(`Claim ${label} approved successfully.`, { scope: 'claims' });
      await fetchClaims(activeSearchTerm);
      setSelectedRows([]);
    } catch (err) {
      console.error('Error approving claim:', err);
      setError(err.response?.data?.error || 'Failed to approve claim');
      showInlineNotice('Failed to approve claim.', { scope: 'claims', type: 'error' });
    }
  };

  const openRejectModal = () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim || claim.status !== 'Submitted') return;
    if (!canReject) return;
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectClaim = async () => {
    if (selectedRows.length !== 1) return;
    const claim = getSelectedClaim();
    if (!claim || claim.status !== 'Submitted') return;
    if (!canReject) return;

    try {
      await axios.post(`${API_BASE_URL}/claims/${claim._id}/reject`, { reason: rejectReason.trim() });
      const label = claim?.claimNumber || claim?._id || 'claim';
      showInlineNotice(`Claim ${label} rejected successfully.`, { scope: 'claims' });
      await fetchClaims(activeSearchTerm);
      setSelectedRows([]);
      setShowRejectModal(false);
      setRejectReason('');
    } catch (err) {
      console.error('Error rejecting claim:', err);
      setError(err.response?.data?.error || 'Failed to reject claim');
      showInlineNotice('Failed to reject claim.', { scope: 'claims', type: 'error' });
      setShowRejectModal(false);
    }
  };

  const handleClaimSaved = async () => {
    await fetchClaims(activeSearchTerm);
    setSelectedRows([]);
  };

  const filteredClaims = claims.filter(claim => {
    if (selectedFilters.status && claim.status !== selectedFilters.status) {
      return false;
    }
    return true;
  });

  const {
    page,
    setPage,
    totalPages,
    pageItems: pagedClaims,
    totalItems,
    pageSize
  } = usePagination(filteredClaims, 10);

  useEffect(() => {
    setSelectedRows([]);
  }, [page, activeSearchTerm]);

  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('editDraft'),
    canSubmit: can('submit'),
    canApprove: can('approve'),
    canReject: can('reject')
  }), [can]);

  const { canCreate, canEdit, canSubmit, canApprove, canReject } = permissions;
  const selectedClaim = selectedRows.length === 1 ? getSelectedClaim() : null;
  const isOwnerSelected = selectedClaim?.createdById === currentUserId;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return formatCurrency(Number(amount) || 0, getNumberingSystemFromStorage(), {
      prefix: getCurrencyPrefixFromStorage()
    });
  };

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Claims <span className="text-muted">({filteredClaims.length})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="claims" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleReload}
                disabled={loading}
                className="recent-invoices-btn recent-invoices-btn-reload"
              >
                {loading ? 'Loading...' : 'Reload'}
              </button>
              <button
                className="recent-invoices-btn recent-invoices-btn-view"
                onClick={handleViewClaim}
                disabled={selectedRows.length === 0}
                title="View claim details"
              >
                View
              </button>
              <button
                className="recent-invoices-btn recent-invoices-btn-copy"
                onClick={handleEditClaim}
                disabled={
                  !canEdit ||
                  selectedRows.length !== 1 ||
                  selectedClaim?.status !== 'Draft' ||
                  !isOwnerSelected
                }
                title="Edit selected draft claim"
              >
                Edit
              </button>
              <button
                className="recent-invoices-btn recent-invoices-btn-filter"
                onClick={() => setShowFilterModal(true)}
                title="Filter claims by status"
              >
                Filter
              </button>
              <button
                className="recent-invoices-btn recent-invoices-btn-quote"
                onClick={handleSubmitClaim}
                disabled={
                  !canSubmit ||
                  selectedRows.length !== 1 ||
                  selectedClaim?.status !== 'Draft' ||
                  !isOwnerSelected
                }
                title="Submit selected claim"
              >
                Submit
              </button>
              {(canApprove || canReject) && (
                <>
                  {canApprove && (
                    <button
                      className="recent-invoices-btn recent-invoices-btn-copy"
                      onClick={handleApproveClaim}
                      disabled={selectedRows.length !== 1 || selectedClaim?.status !== 'Submitted'}
                      title="Approve selected claim"
                      style={{ backgroundColor: '#10b981', color: 'white' }}
                    >
                      Approve
                    </button>
                  )}
                  {canReject && (
                    <button
                      className="recent-invoices-btn recent-invoices-btn-cancel"
                      onClick={openRejectModal}
                      disabled={selectedRows.length !== 1 || selectedClaim?.status !== 'Submitted'}
                      title="Reject selected claim"
                    >
                      Reject
                    </button>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Claim #, Description, Submitted By"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {canCreate && (
                <button
                  onClick={() => setIsClaimModalOpen(true)}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="text-center p-5">Loading...</div>
        ) : filteredClaims.length === 0 ? (
          <div className="recent-invoices-empty">
            {selectedFilters.status
              ? 'No claims match the selected filter'
              : activeSearchTerm
                ? 'No claims match your search'
                : 'No claims yet'}
          </div>
        ) : (
          <table className="recent-invoices-table">
            <thead>
              <tr>
                <th></th>
                <th>Claim #</th>
                <th>Date</th>
                <th>Description</th>
                <th>Amount</th>
                <th>Submitted By</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pagedClaims.map((claim) => (
                <tr
                  key={claim._id}
                  className={selectedRows.includes(claim._id) ? 'selected' : ''}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(claim._id)}
                      onChange={() => handleCheckboxChange(claim._id)}
                    />
                  </td>
                  <td>{claim.claimNumber || '-'}</td>
                  <td>{formatDate(claim.date)}</td>
                  <td>{claim.description}</td>
                  <td>{formatAmount(claim.amount)}</td>
                  <td>{claim.createdByName || 'Unknown'}</td>
                  <td>
                    <StatusLight status={claim.status || 'Draft'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </div>

      <ClaimModal
        isOpen={isClaimModalOpen}
        onClose={() => setIsClaimModalOpen(false)}
        onClaimSaved={handleClaimSaved}
        onClaimSubmitted={handleClaimSaved}
      />

      <ClaimModal
        isOpen={isClaimViewOpen}
        onClose={() => {
          setIsClaimViewOpen(false);
          setClaimToView(null);
        }}
        claim={claimToView}
        onClaimSaved={handleClaimSaved}
        onClaimSubmitted={handleClaimSaved}
      />

      <ClaimModal
        isOpen={isClaimEditOpen}
        onClose={() => {
          setIsClaimEditOpen(false);
          setClaimToEdit(null);
        }}
        claim={claimToEdit}
        isEditable
        onClaimSaved={handleClaimSaved}
        onClaimSubmitted={handleClaimSaved}
      />

      {showFilterModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowFilterModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
              Filter Claims
            </h2>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                Status
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {['Draft', 'Submitted', 'Approved', 'Rejected'].map(status => (
                  <button
                    key={status}
                    onClick={() => setSelectedFilters({ status: selectedFilters.status === status ? null : status })}
                    style={{
                      padding: '10px',
                      backgroundColor: selectedFilters.status === status ? '#0ea5e9' : '#f3f4f6',
                      border: selectedFilters.status === status ? '2px solid #0284c7' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      color: selectedFilters.status === status ? 'white' : '#374151'
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedFilters({ status: null })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0ea5e9',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowRejectModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
              Reject Claim
            </h2>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              Rejection Reason (optional)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '16px',
                resize: 'vertical'
              }}
              placeholder="Enter reason for rejection"
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowRejectModal(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: '#1f2937'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectClaim}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsListPage;
